import React, {useState, useEffect, useRef} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {IoArrowBack, IoMenuOutline, IoRestaurantOutline} from 'react-icons/io5';
import ChatSideMenu from '../../containers/ChatSideMenu';
import ChatMessageInputContainer from '../../containers/ChatMessageInputContainer';
import ChatChunkList from '../../containers/ChatChunkList';
import {
    getChatroomInfo,
    getChatListPaginated,
    connectWebSocket,
    sendChatMessage,
    disconnectWebSocket
} from '../../../api/ChatApi';

import useAuthStore from '../../../store/authStore';
import ChatRoomInfoContainer from "../../containers/ChatRoomInfoContainer";

const ChatPage = () => {
    const {meetingId} = useParams();
    const navigate = useNavigate();
    const {member} = useAuthStore();
    const [chatRoomInfo, setChatRoomInfo] = useState(null);
    const [messageChunks, setMessageChunks] = useState([]); // 페이지네이션된 메시지 청크들
    const [realtimeMessages, setRealtimeMessages] = useState([]); // 웹소켓으로 받은 실시간 메시지들

    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(0);
    const [hasMoreMessages, setHasMoreMessages] = useState(true);
    const [showSideMenu, setShowSideMenu] = useState(false);
    const [membersMap, setMembersMap] = useState(new Map());
    const [blockedIdSet, setBlockedIdSet] = useState(new Set());
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);

    useEffect(() => {
        if (meetingId) {
            fetchChatRoomData();
            setupWebSocket();
        }

        return () => {
            disconnectWebSocket();
        };
    }, [meetingId]);

    const maskIfBlocked = (msg) => {
        try {
            const senderId = msg.senderId ?? msg.sender?.id ?? msg.memberId;
            if (senderId && blockedIdSet && blockedIdSet.has(senderId)) {
                return { ...msg, content: '차단된 사용자입니다' };
            }
            return msg;
        } catch (e) {
            return msg;
        }
    };

    const fetchChatRoomData = async () => {
        try {
            setLoading(true);

            const [roomInfo, chatList] = await Promise.all([
                getChatroomInfo(meetingId),
                getChatListPaginated(meetingId, 0)
            ]);

            setChatRoomInfo(roomInfo);

            // 차단 멤버 ID 리스트 저장
            const idSet = new Set(roomInfo?.blockMemberIdList || []);
            setBlockedIdSet(idSet);

            if (roomInfo?.members) {
                const memberMap = new Map();
                roomInfo.members.forEach(member => {
                    memberMap.set(member.id, member);
                });
                setMembersMap(memberMap);
            }

            // 첫 번째 청크로 설정 (차단 메시지 마스킹)
            if (chatList && chatList.length > 0) {
                const masked = chatList.map(m => ({ ...maskIfBlocked(m) }));
                setMessageChunks([{id: 0, messages: masked}]);
                setHasMoreMessages(chatList.length === 20);
            } else {
                setHasMoreMessages(false);
            }
            setCurrentPage(0);

            requestAnimationFrame(() => scrollToBottom());
        } catch (error) {
            console.error('채팅 데이터 조회 실패:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadMoreMessages = async () => {
        if (!hasMoreMessages) return;

        try {

            const container = messagesContainerRef.current;
            const previousScrollTop = container?.scrollTop || 0;
            const previousScrollHeight = container?.scrollHeight || 0;

            const moreMessages = await getChatListPaginated(meetingId, currentPage + 1);

            if (moreMessages && moreMessages.length > 0) {
                const nextPage = currentPage + 1;
                const masked = moreMessages.map(m => ({ ...maskIfBlocked(m) }));

                setMessageChunks(prev => {
                  const exists = prev.some(chunk => chunk.id === nextPage);
                  if (exists) return prev;
                  return [{ id: nextPage, messages: masked }, ...prev];
                });
                setCurrentPage(nextPage);
                setHasMoreMessages(moreMessages.length === 20);

                const restoreScroll = () => {
                    if (container) {
                        const newScrollHeight = container.scrollHeight;
                        const heightDifference = newScrollHeight - previousScrollHeight;
                        const targetScrollTop = previousScrollTop + heightDifference;

                        if (targetScrollTop >= 0) {
                            container.scrollTop = targetScrollTop;
                        }
                    }
                };

                requestAnimationFrame(restoreScroll);
                setTimeout(restoreScroll, 10);
            } else {
                setHasMoreMessages(false);
            }
        } catch (error) {
            console.error('추가 메시지 로딩 실패:', error);
        }
    };

    const setupWebSocket = async () => {
        try {
            await connectWebSocket(meetingId, handleNewMessage);
        } catch (error) {
            console.error('웹소켓 연결 실패:', error);
        }
    };

    const handleNewMessage = (newMessage) => {
        const messageWithTime = {
            ...newMessage,
            createdAt: newMessage.createdAt || new Date().toISOString(),
            id: newMessage.id || `temp-${Date.now()}-${Math.random()}`
        };

        const masked = maskIfBlocked(messageWithTime);

        const container = messagesContainerRef.current;
        const shouldAutoScroll = container &&
            (container.scrollHeight - container.scrollTop - container.clientHeight < 100);

        setRealtimeMessages(prev => [...prev, masked]);

        if (shouldAutoScroll) {
            requestAnimationFrame(() => scrollToBottom());
        }
    };

    const scrollToBottom = () => {
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
    };

    const handleSendMessage = async (message) => {
        try {
            await sendChatMessage(meetingId, message);
            scrollToBottom();
        } catch (error) {
            console.error('메시지 전송 실패:', error);
            alert('메시지 전송에 실패했습니다.');
        }
    };

    const handleRestaurantCertificate = () => {
        navigate(`/app/chat/${meetingId}/restaurant-certificate`);
    };

    const lastChunkMessage = messageChunks.length > 0
        ? messageChunks[messageChunks.length - 1]?.messages?.slice(-1)[0]
        : null;

    if (loading) {
        return (
            <div className="flex flex-col h-screen bg-amber-50">
                {/* 헤더 */}
                <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center"
                     style={{minHeight: '65px'}}>
                    <button
                        onClick={() => navigate(-1)}
                        className="mr-3 p-1 active:bg-gray-100 rounded-full"
                    >
                        <IoArrowBack size={24} className="text-gray-700"/>
                    </button>

                    <div className="flex-1">
                        <h1 className="text-lg font-semibold text-gray-900">채팅</h1>
                    </div>
                </div>

                {/* 로딩 영역 - 맨 아래 정렬 */}
                <div className="flex-1 flex flex-col justify-end items-center pb-20">
                    <div className="text-gray-500">로딩 중...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-screen bg-amber-50">
            {/* 헤더 */}
            <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center" style={{minHeight: '65px'}}>
                <button
                    onClick={() => navigate(-1)}
                    className="mr-3 p-1 active:bg-gray-100 rounded-full"
                >
                    <IoArrowBack size={24} className="text-gray-700"/>
                </button>

                <div className="flex-1">
                    <h1 className="text-lg font-semibold text-gray-900">
                        {chatRoomInfo?.meetingTitle || '채팅'}
                    </h1>
                </div>

                <button
                    onClick={handleRestaurantCertificate}
                    className="mr-2 p-1 active:bg-gray-100 rounded-full"
                    title="식당 인증"
                >
                    <IoRestaurantOutline size={24} className="text-gray-700"/>
                </button>

                <button
                    onClick={() => setShowSideMenu(true)}
                    className="p-1 active:bg-gray-100 rounded-full"
                >
                    <IoMenuOutline size={24} className="text-gray-700"/>
                </button>
            </div>

            {/* 모임 정보 카드 */}
            {chatRoomInfo && (
                <ChatRoomInfoContainer
                    meetingId={meetingId}
                    chatRoomInfo={chatRoomInfo}
                />
            )}

            {/* 메시지 영역 */}
            <div
                ref={messagesContainerRef}
                className="flex-1 overflow-y-auto px-4 py-4"
            >
                {/* 더보기 버튼 - 메시지가 있고 더 가져올 메시지가 있을 때만 표시 */}
                {hasMoreMessages && (messageChunks.length > 0 || realtimeMessages.length > 0) && (
                                            <div className="flex justify-center py-4">
                            <button
                                onClick={loadMoreMessages}
                                className="px-4 py-2 bg-amber-100 text-gray-700 rounded-lg active:bg-amber-200"
                            >
                                이전 메시지 더보기
                            </button>
                        </div>
                )}

                {messageChunks.length === 0 && realtimeMessages.length === 0 ? (
                    <div className="flex flex_col items-center justify-center h-full">
                        <div className="text-gray-500 text-center">
                            <p className="mb-2">아직 대화가 없습니다</p>
                            <p className="text-sm">첫 메시지를 보내보세요!</p>
                        </div>
                    </div>
                ) : (
                    <>
                        <ChatChunkList
                            messageChunks={messageChunks}
                            realtimeMessages={realtimeMessages}
                            membersMap={membersMap}
                            currentUserId={member?.id}
                            currentPage={currentPage}
                            meetingId={meetingId}
                            placeLatitude={chatRoomInfo.placeLatitude}
                            placeLongitude={chatRoomInfo.placeLongitude}
                            blockedIdSet={blockedIdSet}
                        />
                        <div ref={messagesEndRef}/>
                    </>
                )}
            </div>

            {/* 메시지 입력창 */}
            <ChatMessageInputContainer onSendMessage={handleSendMessage}/>

            {/* 사이드 메뉴 */}
            <ChatSideMenu
                isOpen={showSideMenu}
                onClose={() => setShowSideMenu(false)}
                chatRoomInfo={chatRoomInfo}
                meetingId={meetingId}
                onBlocked={() => fetchChatRoomData()}
            />
        </div>
    );
};

export default ChatPage;
